import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";

import "../css/Modal.css";
import { useLectores } from '../../services/hooks/useLectores';
import { useTurnos } from '../../services/hooks/useTurnos';
import { useComedores } from '../../services/hooks/useComedores';
import { getPeruDateString } from '../../utils/dateUtils';

const ModalTurno = ({
  cliente,
  proyecto,
  comedores,
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState({
    fecha: getPeruDateString(),
    comedor: "",
    servicio: "",
    lector: ""
  });
  const [lectores, setLectores] = useState([]);
  const [serviciosPorComedor, setServiciosPorComedor] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Usar los nuevos hooks
  const { fetchLectoresByComedor } = useLectores();
  const { createTurnoCabecera } = useTurnos();
  const { fetchTipoServiciosPorComedor } = useComedores();

  // Optimizar useEffect para evitar bucles infinitos
  useEffect(() => {
    const loadComedorData = async () => {
      if (formData.comedor) {
        console.log('Cargando datos para comedor ID:', formData.comedor);
        console.log('Proyecto ID:', proyecto.id);
        
        try {
                    
          // Cargar lectores
          console.log('Cargando lectores...');
          const response = await fetchLectoresByComedor(parseInt(formData.comedor));
          console.log('Lectoras cargados:', response);
          setLectores(Array.isArray(response) ? response : []);
          console.log('Lectoras cargados exitosamente');
          
          // Cargar servicios por comedor
          console.log('Cargando servicios por comedor...');
          const servicios = await fetchTipoServiciosPorComedor(parseInt(formData.comedor));
          console.log('Servicios cargados:', servicios);
          setServiciosPorComedor(servicios || []);
          console.log('Datos de los servicios del comedor cargados exitosamente');


        } catch (error) {
          console.error("Error específico al cargar datos del comedor:", error);
          console.error("Stack trace:", error.stack);
          console.error("Mensaje de error:", error.message);
          
          toast.error(`Error al cargar los datos del comedor: ${error.message}`, {
            position: "bottom-right",
            autoClose: 3000
          });
          setLectores([]);
          setServiciosPorComedor([]);
        }
      } else {
        console.log('No hay comedor seleccionado, limpiando datos');
        setLectores([]);
        setServiciosPorComedor([]);
      }
    };

    loadComedorData();
  }, [formData.comedor]); // Remover dependencias que causan bucle infinito

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleComedorChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, comedor: value, servicio: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.comedor || !formData.servicio || !formData.lector) {
      toast.error("Por favor complete todos los campos requeridos", {
        position: "bottom-right",
        autoClose: 2000
      });
      return;
    }

    setLoading(true);
    try {
      const turnoData = {
        id_proyecto: proyecto.id,
        id_comedor: parseInt(formData.comedor),
        id_tipo_servicio_alimentacion: parseInt(formData.servicio),
        id_lector: parseInt(formData.lector),
        fecha_turno: formData.fecha,
        cantidad_comensales: 0,
        cerrado: 0
      };
      
      await createTurnoCabecera(turnoData);
      
      toast.success("Turno creado con éxito", {
        position: "bottom-right",
        autoClose: 1000,
        onClose: () => onClose(true)
      });
    } catch (error) {
      console.error("Error al crear turno:", error);
      
      // Manejar error específico de turno duplicado
      if (error.response && error.response.status === 422 && error.response.data?.error === 'TURNO_DUPLICADO') {
        const turnoExistente = error.response.data.data?.turno_existente;
        const mensaje = turnoExistente 
          ? `Ya existe un turno para el comedor "${turnoExistente.comedor?.nombre}", tipo de servicio "${turnoExistente.tipo_servicio_alimentacion?.descripcion}", lectora "${turnoExistente.lectora?.nombre}" en la fecha seleccionada.`
          : "Ya existe un turno con la misma combinación de comedor, tipo de servicio, lectora y fecha.";
          
        toast.error(mensaje, {
          position: "bottom-right",
          autoClose: 4000
        });
      } else if (error.response && error.response.data?.message) {
        toast.error(error.response.data.message, {
          position: "bottom-right",
          autoClose: 3000
        });
      } else {
        toast.error("Error al crear el turno", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Digital Harmony Styles for Modal */}
      <style>
        {`
          /* Modal backdrop personalizado */
          .dh-modal-backdrop {
            background: linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%);
            backdrop-filter: blur(8px);
          }
          
          /* Modal content con Digital Harmony */
          .dh-modal-content {
            border: none;
            border-radius: 20px;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
            background: white;
            overflow: hidden;
          }
          
          /* Header del modal */
          .dh-modal-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 1.5rem 2rem;
            border: none;
            position: relative;
          }
          
          .dh-modal-header::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 1px;
            background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.3) 50%, transparent 100%);
          }
          
          .dh-modal-title {
            color: white;
            font-weight: 700;
            font-size: 1.5rem;
            margin: 0;
          }
          
          /* Close button personalizado */
          .dh-close-btn {
            background: rgba(255, 255, 255, 0.2);
            border: 2px solid rgba(255, 255, 255, 0.3);
            border-radius: 12px;
            color: white;
            padding: 8px 12px;
            font-weight: 600;
            transition: all 0.3s ease;
          }
          
          .dh-close-btn:hover {
            background: rgba(255, 255, 255, 0.3);
            border-color: rgba(255, 255, 255, 0.5);
            color: white;
            transform: scale(1.05);
          }
          
          /* Body del modal */
          .dh-modal-body {
            padding: 2rem;
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          }
          
          /* Form labels mejorados */
          .dh-form-label {
            font-weight: 700;
            color: #374151;
            margin-bottom: 0.75rem;
            font-size: 0.95rem;
            display: block;
          }
          
          /* Form controls mejorados */
          .dh-form-control, .dh-form-select {
            border: 2px solid #e5e7eb;
            border-radius: 12px;
            padding: 12px 16px;
            font-size: 1rem;
            transition: all 0.3s ease;
            background: white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }
          
          .dh-form-control:focus, .dh-form-select:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.25rem rgba(102, 126, 234, 0.15);
            background: white;
            outline: none;
          }
          
          .dh-form-control:disabled, .dh-form-select:disabled {
            background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
            border-color: #d1d5db;
            color: #6b7280;
            cursor: not-allowed;
          }
          
          .dh-form-select:disabled option {
            color: #9ca3af;
            font-style: italic;
          }
          
          /* Footer del modal */
          .dh-modal-footer {
            padding: 1.5rem 2rem;
            background: white;
            border-top: 1px solid #f3f4f6;
            display: flex;
            justify-content: flex-end;
            gap: 1rem;
          }
          
          /* Botones Digital Harmony */
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 24px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
            font-size: 1rem;
          }
          
          .btn-dh-primary:hover:not(:disabled) {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-primary:disabled {
            background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 12px 24px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
            font-size: 1rem;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          /* Required asterisk styling */
          .text-danger {
            color: #ef4444 !important;
            font-weight: 700;
          }
          
          /* Form group spacing */
          .dh-form-group {
            margin-bottom: 1.5rem;
          }
          
          .dh-form-group:last-child {
            margin-bottom: 0;
          }
          
          /* Spinner personalizado */
          .dh-spinner {
            display: inline-block;
            width: 1rem;
            height: 1rem;
            border: 0.15rem solid rgba(255, 255, 255, 0.3);
            border-radius: 50%;
            border-top-color: white;
            animation: spin 1s ease-in-out infinite;
            margin-right: 0.5rem;
          }
          
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
      
      <div className="modal fade show d-block dh-modal-backdrop" tabIndex="-1">
        <ToastContainer />
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content dh-modal-content">
            <div className="modal-header dh-modal-header">
              <h5 className="modal-title dh-modal-title">Crear Nuevo Turno</h5>
              <button 
                type="button" 
                className="btn dh-close-btn" 
                onClick={onClose}
                aria-label="Close"
              >
                ✕
              </button>
            </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body dh-modal-body">
              <div className="dh-form-group">
                <label className="dh-form-label">Proyecto</label>
                <input 
                  type="text" 
                  className="form-control dh-form-control" 
                  value={proyecto.nombre} 
                  disabled 
                />
              </div>

              <div className="dh-form-group">
                <label className="dh-form-label">Comedor <span className="text-danger">*</span></label>
                <select
                  name="comedor"
                  className="form-select dh-form-select"
                  value={formData.comedor}
                  onChange={handleComedorChange}
                  required
                >
                  <option value="">Selecciona un comedor</option>
                  {comedores?.map((comedor) => (
                    <option key={comedor.id} value={comedor.id}>
                      {comedor.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dh-form-group">
                <label className="dh-form-label">Tipo de Servicio <span className="text-danger">*</span></label>
                <select
                  name="servicio"
                  className="form-select dh-form-select"
                  value={formData.servicio}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.comedor}
                >
                  <option value="">Selecciona un tipo de servicio</option>
                  {serviciosPorComedor.map((servicio) => (
                    <option key={servicio.id} value={servicio.id}>
                      {servicio.descripcion}
                    </option>
                  ))}
                </select>
              </div>

              <div className="dh-form-group">
                <label className="dh-form-label">Lectora <span className="text-danger">*</span></label>
                <select
                  name="lector"
                  className="form-select dh-form-select"
                  value={formData.lector}
                  onChange={handleInputChange}
                  required
                  disabled={!formData.comedor}
                >
                  <option value="">Selecciona una lectora</option>
                  {Array.isArray(lectores) && lectores.length > 0 ? (
                    lectores.map((lector) => (
                      <option key={lector.id} value={lector.id}>
                        {lector.nombre}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>
                      {!formData.comedor 
                        ? "Primero selecciona un comedor"
                        : "No hay lectoras disponibles para este comedor"
                      }
                    </option>
                  )}
                </select>
              </div>

              <div className="dh-form-group">
                <label className="dh-form-label">Fecha</label>
                <input
                  type="date"
                  name="fecha"
                  className="form-control dh-form-control"
                  value={formData.fecha}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="modal-footer dh-modal-footer">
              <button 
                type="button" 
                className="btn btn-dh-secondary" 
                onClick={onClose}
              >
                Cancelar
              </button>
              <button 
                type="submit" 
                className="btn btn-dh-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="dh-spinner"></div>
                    Guardando...
                  </>
                ) : (
                  'Guardar Turno'
                )}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalTurno;
